<template>
  <v-data-table
    :headers="headers"
    :items="users"
    :sort-by="[{ key: '#', order: 'asc' }]"
  >
    <!-- Top bar: search + New button -->
    <template v-slot:top>
      <v-toolbar flat class="bg-white px-4 py-3">
        <v-row align="center" justify="space-between" no-gutters>
          <!-- Search -->
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search"
              density="comfortable"
              variant="outlined"
              hide-details
              append-inner-icon="mdi-magnify"
              label="Search users..."
              @input="getUsers"
            />
          </v-col>

          <!-- New User -->
          <v-col cols="12" sm="3" class="text-right">
            <v-btn
              variant="elevated"
              color="blue"
              size="large"
              prepend-icon="mdi-plus-thick"
              class="rounded-pill px-6 font-semibold text-white"
              @click="dialog = true"
            >
              New User
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>
    </template>

    <!-- Row numbering -->
    <template v-slot:[`item.id`]="{ index }">
      {{ index + 1 }}
    </template>

    <!-- Active status -->
    <template v-slot:[`item.active`]="{ item }">
      <v-chip
        :color="item.active ? 'success' : 'grey lighten-2'"
        text-color="white"
        small
      >
        {{ item.active ? "Active" : "Deactivated" }}
      </v-chip>
    </template>

    <!-- Actions -->
    <template v-slot:[`item.actions`]="{ item }">
      <v-btn
        icon
        size="small"
        variant="flat"
        color="primary"
        @click="editItem(item)"
      >
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <v-btn
        icon
        size="small"
        variant="flat"
        color="error"
        @click="deleteItem(item)"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </template>

    <!-- No data -->
    

    <template #no-data>
      <v-card flat class="text-center pa-4">
        <v-icon size="36" color="grey">mdi-database-remove</v-icon>
        <div class="text-subtitle-2 mt-2">No users found</div>
        <v-btn color="black" class="mt-2" @click="getUsers">
          Reset
        </v-btn>
      </v-card>
    </template>
  </v-data-table>

  <!-- Add / Edit Dialog -->
  <v-dialog v-model="dialog" max-width="500px">
    <v-card>
      <v-card-title class="text-h6">
        {{ formTitle }}
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="isValid" @submit.prevent="save">
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="editedItem.fullName"
                label="Full Name"
                :rules="rules.fullName"
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editedItem.email"
                label="Email"
                :rules="rules.email"
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="editedItem.roleId"
                :items="roles"
                item-text="title"
                item-value="id"
                label="Role"
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editedItem.password"
                type="password"
                label="Default Password"
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="editedItem.active"
                :items="status"
                item-text="title"
                item-value="value"
                label="Status"
                dense
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey" @click="close"> Cancel </v-btn>
        <v-btn
          variant="elevated"
          color="black"
          class="rounded-pill px-6 font-semibold text-white"
          :disabled="!isValid"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete Confirmation -->
  <v-dialog v-model="dialogDelete" max-width="400px">
    <v-card>
      <v-card-title class="text-h6 text-center">
        Confirm Deletion
      </v-card-title>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey" @click="closeDelete"> Cancel </v-btn>
        <v-btn
          variant="elevated"
          color="error"
          class="rounded-pill px-6 font-semibold text-white"
          @click="deleteItemConfirm"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import UsersService from "../services/user.service.js";
import RolesService from "../services/role.services.js";
export default {
  data: () => ({
    dialog: false,
    dialogDelete: false,
    isValid: false,
    rules: {
      fullName: [
        (value) => {
          if (value?.length >= 3) return true;
          return "You must enter a full name.";
        },
      ],
      email: [
        (value) => {
          if (value?.length >= 3) return true;
          return "You must enter a email.";
        },
      ],
    },
    headers: [
      {
        title: "#",
        value: "id",
        align: "start",
        sortable: false,
        key: "id",
      },
      { title: "Name", value: "fullName", key: "fullName" },
      { title: "Email", value: "email", key: "email" },
      // { title: "Phone Number", value: "phoneNumber", key: "phoneNumber" },
      { title: "Role", value: "role.title", key: "role.title" },
      { title: "Status", value: "active", key: "active" },
      { title: "Actions", key: "actions", sortable: false },
    ],

    status: [
      {
        title: "Active",
        value: 1,
      },
      {
        title: "Deactivated",
        value: 0,
      },
    ],

    editedIndex: -1,
    users: [],
    roles: [],
    editedItem: {
      id: "",
      fullName: "",
      email: "",
      active: 1,
      description: "",
      password: "",
    },
    defaultItem: {
      id: "",
      fullName: "",
      email: "",
      active: 0,
      description: "",
      password: "",
    },
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New User" : "Edit User";
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  created() {
    this.getUsers();
    this.getRoles();
  },

  methods: {
    async validate() {
      this.isValid = await this.$refs.form.validate();
    },

    handleLogout() {
      this.$store.commit("logout");
      this.$store.dispatch("resetInactivityTimeout");
      this.$store.dispatch("auth/logout");
      this.$store.dispatch("resetInactivityTimeout");
      this.$router.push("/login");
      this.$store.dispatch("alert/success", "Logout Successfully");
    },

    getUsers() {
      return UsersService.index().then(
        (response) => {
          if (response.status == 200) {
            this.users = response.data.data;
            this.loading = false;
          } else {
            this.users = [];
            this.loading = false;
            console.log(response.data.console.error);
          }
        },
        (error) => {
          this.users = [];
          if (error.response.status == 401) {
            this.handleLogout();
          }
          console.log(error);
          this.$store.dispatch("alert/error", error.message);
        }
      );
    },

    getRoles() {
      return RolesService.index().then(
        (response) => {
          if (response.status == 200) {
            this.roles = response.data.data;
            this.loading = false;
          } else {
            this.roles = [];
            this.loading = false;
            console.log(response.data.console.error);
          }
        },
        (error) => {
          this.roles = [];
          if (error.response.status == 401) {
            this.handleLogout();
          }
          console.log(error);
          this.$store.dispatch("alert/error", error.message);
        }
      );
    },

    editItem(item) {
      this.editedIndex = this.users.findIndex((u) => u.id === item.id);
      this.editedItem = {
        id: item.id,
        fullName: item.fullName,
        email: item.email,
        roleId: item.role?.id || null,
        active: item.active ? 1 : 0,
      };
      this.dialog = true;
      this.$nextTick(() => this.$refs.form?.validate());
    },

    deleteItem(item) {
      this.editedIndex = this.users.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    async deleteItemConfirm() {
      try {
        const { data } = await UsersService.delete({ id: this.editedItem.id });
        this.$store.dispatch("alert/success", data.message);
        this.closeDelete();
        await this.getUsers();
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.$store.dispatch("alert/error", msg);
      }
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.$refs.form?.resetValidation();
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
        this.isValid = false;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    async save() {
      await this.validate();
      if (!this.isValid) return;

      try {
        const svc =
          this.editedIndex > -1
            ? UsersService.update(this.editedItem)
            : UsersService.store(this.editedItem);

        const { data, status } = await svc;
        this.$store.dispatch("alert/success", data.message);
        this.close();
        await this.getUsers();
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        this.$store.dispatch("alert/error", msg);
      }
    },
  },
};
</script>
